import { useEffect } from "react";
import { useStores } from "stores/setup/use-store";
import useFetchInsightMultiStation from "../../../hooks/useFetchInsightMultiStation";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import useReloadMultiStationInsight from "./hooks/useReloadMultiStationInsight";
import useCustomMarkers from "./hooks/useCustomMarkers";
import useMapCenter from "./hooks/useMapCenter";
import { observer } from "mobx-react-lite";
import ForecastChartPanelMap from "./ForecastChartPanelMap";
import { customMarkerUserIconSelected } from "./util/customMarkerUserIconSelected";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import { getConfig } from "config/app.config";
import { ConfigKey } from "enum/ConfigKey";

const ForecastChartPanelMapContainer = () => {
  const {
    dataStores: {
      forecastChartPageStore,
      forecastChartInsightStore,
      forecastChartMeteogramStore,
      regionStore,
      radarMapStore,
      forecastMapStore,
      userConfigStore,
      insightAlertStore,
    },
  } = useStores();

  useFetchInsightMultiStation(
    forecastChartInsightStore,
    forecastChartPageStore,
    forecastChartMeteogramStore.selectedInsight?.id,
  );

  const customMarker = useCustomMarkers();

  useEffect(() => {
    const initialMapLayer = localStorage.getItem("initialMapLayer");

    if (initialMapLayer !== "noInitialMapLayer") {
      const initialVariable =
        initialMapLayer ?? EnvironmentalVariables.WIND_VEL;

      if (initialVariable === EnvironmentalVariables.RADAR) {
        radarMapStore.setIsInitialLayer(true);
        radarMapStore.setShowRadar(true);
        forecastChartPageStore.setMapZoom(7); // Set zoom to 7 for optimal radar tile resolution

        forecastMapStore.setLayersData(null);
        forecastMapStore.setSelectedEnvironmentalVariable(null);
        forecastMapStore.setShow(false);
      } else {
        forecastMapStore.setIsInitialLayer(true);
        forecastMapStore.setSelectedEnvironmentalVariable(
          initialVariable as EnvironmentalVariables,
        );
        forecastMapStore.setShow(true);
      }
    }
  }, []);

  const center = useMapCenter();

  const iconSelected = customMarkerUserIconSelected;

  const reloadMultiStationInsight = useReloadMultiStationInsight({
    forecastChartPageStore,
    forecastChartInsightStore,
    forecastChartMeteogramStore,
    regionStore,
    userConfigStore,
  });

  useEffect(() => {
    const intervalId = setInterval(reloadMultiStationInsight, 10 * 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ForecastChartPanelMap({
    center,
    zoom: forecastChartPageStore.mapZoom,
    customMarker,
    tabStateActivty: forecastChartPageStore.tabStateActivty,
    loadingInsightMultiStation:
      forecastChartInsightStore.loadingInsightMultiStation,
    clickMapLatLng: forecastChartPageStore.clickMapLatLng,
    selectedStation: forecastChartPageStore.selectedStation,
    iconSelected,
    insightMode: forecastChartPageStore.insightMode,
    insightMultiStation: forecastChartInsightStore.insightMultiStation,
    visualizationType: forecastChartMeteogramStore.visualizationType,
    regionId: forecastChartPageStore.regionId,
    sensorMarkerData: forecastChartPageStore.isSensorSelectionMode
      ? insightAlertStore.sensorMeasuredData
      : forecastChartPageStore.sensorMarkerData,
    mapCenterLatitude: forecastChartPageStore.mapCenterLatitude,
    mapCenterLongitude: forecastChartPageStore.mapCenterLongitude,
    searchedLatLng: forecastChartPageStore.searchedLatLng,
    radarTileUrl: radarMapStore.currentTileUrl, // Rainbow AI
    forecastTileLayerSource: forecastMapStore.currentMapUrl,
    showRadarTile: radarMapStore.showRadar,
    showForecastTile: forecastMapStore.show,
    maxZoom: forecastMapStore.maxZoomMap,
    clearSearchedLatLng: () => forecastChartPageStore.setSearchedLatLng(null),
    selectedLayer: forecastMapStore.selectedEnvironmentalVariable,
    mapTilerKey: getConfig(ConfigKey.REACT_APP_MAPTILER_KEY),
    isAreaDrawingMode: forecastChartPageStore.isAreaDrawingMode,
    isPointSelectionMode: forecastChartPageStore.isPointSelectionMode,
    isSensorSelectionMode: forecastChartPageStore.isSensorSelectionMode,
    selectedSensorIds: forecastChartPageStore.selectedSensorIds,
  });
};

export default observer(ForecastChartPanelMapContainer);
